import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UsersRound, CheckCircle2, XCircle, UserX, Percent, Filter, RotateCcw, Download, Search, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import Pagination from "../../hooks/Pagination";
import downloadBlob from "../../utils/downloadBlob";
import { exportRegisteredMembers } from "../../services/api";
import { fetchRegisteredMembers } from "../../store/reportSlices/membersReportSlice";

const PER_PAGE = 25;

const buildPayload = (f) => ({
    from_date: f.fromDate || null,
    to_date: f.toDate || null,
    membership: f.membership || null,
    keyword: (f.keyword || "").trim() || null,
});
const defaultFilters = () => ({ fromDate: "", toDate: "", membership: "", keyword: "" });

const BADGE = {
    active: { label: "Active member", chip: "bg-green-50 text-green-700" },
    expired: { label: "Expired / inactive", chip: "bg-slate-100 text-slate-600" },
    none: { label: "Not purchased", chip: "bg-rose-50 text-rose-600" },
};
const CARD_ACCENT = {
    amber: "bg-amber-50 text-amber-600",
    green: "bg-green-50 text-green-600",
    slate: "bg-slate-100 text-slate-600",
    rose: "bg-rose-50 text-rose-600",
    blue: "bg-blue-50 text-blue-600",
};
const num = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
const customerName = (r) => (r.name || `${r.fname || ""} ${r.lname || ""}`).trim() || "Customer";

/* Cards double as quick filters (click = filter the table by that bucket) */
const StatCard = ({ icon: Icon, label, value, sub, accent, onClick, selected }) => {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag type={onClick ? "button" : undefined} onClick={onClick}
            className={`bg-white rounded-xl shadow p-4 flex items-center gap-3 text-left transition ${onClick ? "hover:shadow-md" : ""} ${selected ? "ring-2 ring-[#f4a406]" : ""}`}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${CARD_ACCENT[accent]}`}><Icon size={22} /></div>
            <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
                {sub && <p className="text-xs text-gray-400">{sub}</p>}
            </div>
        </Tag>
    );
};

export default function RegisteredMembers() {
    useDocumentTitle("Registered Members | Reports");
    const dispatch = useDispatch();
    const { rows, summary, total, lastPage, loading, error } = useSelector((s) => s.membersReport.registered);

    const [showFilters, setShowFilters] = useState(false);
    const [draft, setDraft] = useState(defaultFilters());
    const [applied, setApplied] = useState(defaultFilters());
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);

    const load = () => dispatch(fetchRegisteredMembers({ perPage: PER_PAGE, page, filters: buildPayload(applied) }));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, page, applied]);

    const handleDraft = (e) => setDraft((p) => ({ ...p, [e.target.name]: e.target.value }));

    const apply = (next) => {
        setPage(1);
        setApplied(next);
    };

    const handleApply = (e) => {
        e.preventDefault();
        toastr.options = { closeButton: true, progressBar: true, timeOut: 3000, positionClass: "toast-top-center" };
        if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) {
            toastr.error("From date cannot be after To date");
            return;
        }
        apply({ ...draft });
    };

    const handleReset = () => {
        const fresh = defaultFilters();
        setDraft(fresh);
        apply(fresh);
    };

    const pickBucket = (membership) => {
        const next = { ...applied, membership: applied.membership === membership ? "" : membership };
        setDraft((d) => ({ ...d, membership: next.membership }));
        apply(next);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            downloadBlob(await exportRegisteredMembers(buildPayload(applied)), "registered-members.csv");
        } catch {
            toastr.error("Unable to export. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const s = summary || { total: 0, active: 0, expired: 0, not_purchased: 0, conversion_rate: 0 };
    const activeFilters = ["fromDate", "toDate", "membership", "keyword"].filter((k) => applied[k]).length;

    return (
        <div className="w-full space-y-4">
            {/* Header */}
            <div className="rounded-xl p-4 text-white shadow-lg bg-gradient-to-r from-[#f4a406] to-[#d89005]">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center"><UsersRound size={22} /></div>
                    <div>
                        <h3 className="text-xl font-bold leading-tight">Registered Members</h3>
                        <p className="text-sm text-white/90">GoerOne registered customers and whether they bought a paid membership</p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <button type="button" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors">
                            <Filter size={16} /> {showFilters ? "Hide filters" : "Filters"}
                            {activeFilters > 0 && (
                                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#d89005] text-xs font-bold inline-flex items-center justify-center">{activeFilters}</span>
                            )}
                        </button>
                        <button type="button" onClick={handleExport} disabled={exporting || loading || !total}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#d89005] text-sm font-medium hover:bg-white/90 disabled:opacity-60">
                            <Download size={16} /> {exporting ? "Exporting…" : "Export Excel"}
                        </button>
                    </div>
                </div>
            </div>

            {showFilters && (
                <form onSubmit={handleApply} className="bg-white rounded-xl shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Registered from</label>
                            <input type="date" name="fromDate" value={draft.fromDate} onChange={handleDraft}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Registered to</label>
                            <input type="date" name="toDate" value={draft.toDate} min={draft.fromDate || undefined} onChange={handleDraft}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Membership</label>
                            <select name="membership" value={draft.membership} onChange={handleDraft}
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
                                <option value="">All members</option>
                                <option value="active">Active membership</option>
                                <option value="expired">Expired / inactive</option>
                                <option value="none">Not purchased</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                            <div className="relative">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" name="keyword" value={draft.keyword} onChange={handleDraft} placeholder="Name, email or mobile"
                                    className="w-full p-2 pl-9 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end mt-4 space-x-3">
                        <button type="button" onClick={handleReset} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                            <RotateCcw size={15} /> Reset
                        </button>
                        <button type="submit" className="px-4 py-2 bg-[#f4a406] text-white rounded-md hover:bg-[#d89005]">Apply Filters</button>
                    </div>
                </form>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-center gap-3 text-sm">
                    <AlertTriangle size={18} /> <span>{error}</span>
                    <button onClick={load} className="ml-auto underline">Retry</button>
                </div>
            )}

            {/* Summary cards (click to filter) */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard icon={UsersRound} label="Registered Members" value={num(s.total)} accent="blue" onClick={() => pickBucket("")} selected={!applied.membership} />
                <StatCard icon={CheckCircle2} label="Active Membership" value={num(s.active)} accent="green" onClick={() => pickBucket("active")} selected={applied.membership === "active"} />
                <StatCard icon={XCircle} label="Expired / Inactive" value={num(s.expired)} accent="slate" onClick={() => pickBucket("expired")} selected={applied.membership === "expired"} />
                <StatCard icon={UserX} label="Not Purchased" value={num(s.not_purchased)} accent="rose" onClick={() => pickBucket("none")} selected={applied.membership === "none"} />
                <StatCard icon={Percent} label="Purchase Rate" value={`${s.conversion_rate}%`} sub="bought a paid plan" accent="amber" />
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase text-gray-500 bg-gray-50">
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Contact</th>
                            <th className="px-4 py-3">Registered</th>
                            <th className="px-4 py-3">Membership</th>
                            <th className="px-4 py-3">Latest paid plan</th>
                            <th className="px-4 py-3">Valid till</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6}><ShimmerLoader /><ShimmerLoader /><ShimmerLoader /></td></tr>}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-gray-500 py-10">No registered members found for the selected filters.</td></tr>
                        )}
                        {!loading && rows.map((r) => (
                            <tr key={r.id} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-800">{customerName(r)}</td>
                                <td className="px-4 py-3 text-gray-600">{r.email || "—"}<br /><span className="text-xs text-gray-400">{r.mobile}</span></td>
                                <td className="px-4 py-3">{fmtDate(r.created_at)}</td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${BADGE[r.membership_state]?.chip}`}>
                                        {BADGE[r.membership_state]?.label || "—"}
                                    </span>
                                </td>
                                <td className="px-4 py-3">{r.plan_name || "—"}</td>
                                <td className="px-4 py-3">{fmtDate(r.plan_valid_till)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {lastPage > 1 && (
                    <div className="px-4 py-3 border-t">
                        <Pagination totalPages={lastPage} currentPage={page} setCurrentPage={setPage} />
                    </div>
                )}
            </div>
        </div>
    );
}